import React, { useEffect, useMemo, useState } from 'react';
import { UpdateReport } from 'src/api';
import { ButtonGroup, Switch } from 'src/components/atoms';
import { DropdownSelect, FormInput } from 'src/components/molecules';
import { HeaderSectionTitle } from 'src/components/organismsLegacy/CaseScreenHeader/CaseScreenHeader.styles';
import { useRelationshipTypeQuery, UserType } from 'src/graphql';
import { useSearchUsers } from 'src/hooks';
import { FormInputVariant, FormState } from 'src/types';
import * as Validator from 'validator';
import {
  AnonymousContainer,
  DetailsContainer,
  ErrorContainer,
  ReporterContainer,
  Subtitle,
  SwitchLabel,
} from './DraftCaseReporter.styles';

const enum tabOptions {
  EmployeeDirectory = 'Employee directory',
  Manual = 'Manual',
}

// react-select renders every option into the DOM (no virtualization); capping
// the list keeps each keystroke's re-render cheap for orgs with large directories.
const MAX_DIRECTORY_OPTIONS = 25;

interface Props {
  currentReport: UpdateReport | null;
  updateReport: (report: UpdateReport) => void;
  isUpdateRelationshipToOrganisationEnabled?: boolean;
}

export const DraftCaseReporter = ({
  currentReport,
  isUpdateRelationshipToOrganisationEnabled,
  ...props
}: Props) => {
  const [isExternal, setIsExternal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<tabOptions>(
    isExternal ? tabOptions.Manual : tabOptions.EmployeeDirectory,
  );
  const [relationshipToCompany, setRelationshipToCompany] = useState<
    { label: string; value: number } | undefined
  >(undefined);
  const [anonymousToggle, setAnonymousToggle] = useState(
    currentReport?.anonymous || false,
  );
  const [selectedUserName, setSelectedUserName] = useState(
    isExternal ? currentReport?.User?.name : undefined,
  );
  const [selectedUserEmail, setSelectedUserEmail] = useState(
    isExternal ? currentReport?.User?.email : undefined,
  );
  const [selectedUser, setSelectedUser] = useState<UserType | undefined>(
    undefined,
  );
  const [isEmailValid, setIsEmailValid] = useState(true);

  const { data: relationshipQueryType } = useRelationshipTypeQuery();
  const selectedRelationshipId = currentReport?.relationshipToCompany;
  const selectedRelationship = relationshipQueryType?.getRelationshipType?.find(
    (relationship) => relationship.id === selectedRelationshipId,
  );
  const reporterPresent = Boolean(currentReport?.User?.id);
  const disabledReporterError =
    'You cannot change the reporter once the report has been saved. To create a report using an alternate user, please create a new report.';

  useEffect(() => {
    if (currentReport?.User) {
      setSelectedUser(currentReport.User as UserType);
      setSelectedUserName(isExternal ? currentReport.User.name : undefined);
      setSelectedUserEmail(isExternal ? currentReport.User.email : undefined);
    }
  }, [currentReport?.User, isExternal]);

  useEffect(() => {
    if (currentReport?.anonymous || currentReport?.anonymous === false) {
      setAnonymousToggle(currentReport.anonymous);
    }
  }, [currentReport?.anonymous]);

  useEffect(() => {
    if (selectedRelationship && !relationshipToCompany) {
      setRelationshipToCompany({
        label: String(selectedRelationship.name),
        value: Number(selectedRelationship.id),
      });
    }
  }, [selectedRelationship, relationshipToCompany]);

  const currentUserId = currentReport?.User?.id;
  const currentUserEid = currentReport?.User?.eid;

  useEffect(() => {
    const isUserSelectedExternal = currentUserId && !currentUserEid;
    setIsExternal(Boolean(isUserSelectedExternal));
    setSelectedTab(
      isUserSelectedExternal ? tabOptions.Manual : tabOptions.EmployeeDirectory,
    );
  }, [currentUserId, currentUserEid]);

  const updateReporter = ({
    internalUser,
    selectedUser,
    email,
    name,
  }: {
    internalUser: boolean;
    selectedUser?: UserType;
    email?: string;
    name?: string;
  }) => {
    if (internalUser) {
      setSelectedUser(selectedUser);
      props.updateReport({
        ...currentReport,
        ...(selectedUser?.id && {
          userUpdates: undefined,
          User: undefined,
          user: `${selectedUser?.id}`,
        }),
      });
    } else if (email || selectedUserEmail) {
      if (email) {
        const isValid = Validator.isEmail(email);
        setIsEmailValid(isValid);
        if (!isValid) {
          return;
        }
      }
      setSelectedUser(undefined);
      props.updateReport({
        ...currentReport,
        User: undefined,
        user: undefined,
        userUpdates: {
          ...currentReport?.userUpdates,
          email: email || selectedUserEmail,
          name: name || selectedUserName,
        },
      });
    }
  };

  const onUpdateRelationshipToCompany = ({
    relationship,
  }: {
    relationship: {
      label: string;
      value: number;
    };
  }) => {
    const relationshipValue =
      isUpdateRelationshipToOrganisationEnabled && relationship?.value
        ? relationship?.value
        : undefined;
    setRelationshipToCompany(relationship);
    props.updateReport({
      ...currentReport,
      relationshipToCompany: relationshipValue,
    });
  };

  const onUpdateAnonymousToggle = (anonymous: boolean) => {
    setAnonymousToggle(anonymous);
    props.updateReport({
      ...currentReport,
      anonymous,
    });
  };

  const { searchUsers, sortedValidUsers } = useSearchUsers(
    [],
    [],
    false,
    false,
    null,
  );

  const directoryOptions = useMemo(() => {
    const base = sortedValidUsers.slice(0, MAX_DIRECTORY_OPTIONS);
    const users =
      selectedUser?.eid && !base.some((user) => user.id === selectedUser.id)
        ? [selectedUser, ...base]
        : base;
    return users.map((user) => ({
      label: user.name || 'Anonymous',
      value: String(user.id),
      user,
    }));
  }, [sortedValidUsers, selectedUser]);

  const directoryValue = useMemo(
    () =>
      selectedUser?.eid
        ? {
            label: selectedUser.name || 'Anonymous',
            value: String(selectedUser.id),
          }
        : null,
    [selectedUser],
  );

  return (
    <ReporterContainer>
      <HeaderSectionTitle>Reporter details</HeaderSectionTitle>
      <ButtonGroup
        buttonGroupOptionTestId="reporter-source-tabs"
        options={[tabOptions.EmployeeDirectory, tabOptions.Manual]}
        setSelectedOption={(option) => setSelectedTab(option)}
        size="medium"
        selectedOption={selectedTab}
        fullWidth={true}
        disabled={reporterPresent}
      />
      <DetailsContainer>
        {selectedTab === tabOptions.EmployeeDirectory && (
          <div>
            <Subtitle>Name</Subtitle>
            <DropdownSelect
              isSearchable={true}
              state={reporterPresent ? FormState.DISABLED : FormState.DEFAULT}
              disabled={reporterPresent}
              onInputChange={(value: string) => searchUsers(value)}
              disableFilter={true}
              options={directoryOptions}
              value={directoryValue}
              onChange={(option) => {
                if (option && 'user' in option) {
                  const user = option.user as UserType;
                  updateReporter({
                    internalUser: true,
                    selectedUser: user,
                  });
                }
              }}
              placeholder="Select employee from directory"
              buttonVariant="secondary"
              data-testid="reporter-select-dropdown"
            />
            {reporterPresent && (
              <ErrorContainer data-testid="disabled-reporter-error">
                {disabledReporterError}
              </ErrorContainer>
            )}
          </div>
        )}
        {selectedTab === tabOptions.Manual && (
          <FormInput
            label="Name"
            variant={FormInputVariant.INPUT}
            placeholder="Enter reporter’s name"
            data-testid="name-input"
            state={reporterPresent ? FormState.DISABLED : FormState.DEFAULT}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setSelectedUserName(event.target.value);
            }}
            onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
              updateReporter({
                internalUser: false,
                name: event.target.value,
              });
            }}
            type="text"
            value={selectedUserName}
            $removeMarginBottom={true}
            disabled={reporterPresent}
          />
        )}
        {selectedTab === tabOptions.Manual && (
          <div>
            <FormInput
              isRequired={true}
              label="E-mail address"
              variant={FormInputVariant.INPUT}
              placeholder="Enter reporter’s e-mail address"
              data-testid="email-input"
              state={
                reporterPresent
                  ? FormState.DISABLED
                  : selectedUserName && !selectedUserEmail
                    ? FormState.ERROR
                    : FormState.DEFAULT
              }
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setSelectedUserEmail(event.target.value);
              }}
              onBlur={(event: React.ChangeEvent<HTMLInputElement>) => {
                updateReporter({
                  internalUser: false,
                  email: event.target.value,
                });
              }}
              type="email"
              value={selectedUserEmail}
              $removeMarginBottom={true}
              disabled={reporterPresent}
            />
            {reporterPresent && (
              <ErrorContainer data-testid="disabled-reporter-error">
                {disabledReporterError}
              </ErrorContainer>
            )}
            {!isEmailValid && (
              <ErrorContainer color="red" data-testid="email-error">
                {'email address is not valid'.toUpperCase()}
              </ErrorContainer>
            )}
          </div>
        )}
        {isUpdateRelationshipToOrganisationEnabled && (
          <div>
            <Subtitle>Relationship to organisation</Subtitle>
            <DropdownSelect
              options={
                relationshipQueryType?.getRelationshipType?.map(
                  (relationship) => ({
                    label: relationship.name,
                    value: relationship.id,
                  }),
                ) ?? []
              }
              value={relationshipToCompany}
              onChange={(option) => {
                if (option && !Array.isArray(option) && 'label' in option) {
                  const relationship = {
                    label: String(option.label),
                    value: Number(option.value),
                  };
                  onUpdateRelationshipToCompany({
                    relationship,
                  });
                }
              }}
              placeholder="Select relationship to organisation"
              buttonVariant="secondary"
              data-testid="relationship-to-organisation-dropdown"
            />
          </div>
        )}
        <AnonymousContainer>
          <Switch
            data-testid="reporter-anonymous-toggle"
            onClick={() => {
              onUpdateAnonymousToggle(!anonymousToggle);
            }}
            checked={anonymousToggle}
          />
          <SwitchLabel>Mark reporter as anonymous</SwitchLabel>
        </AnonymousContainer>
      </DetailsContainer>
    </ReporterContainer>
  );
};
